/**
 * pk-facets-mobile.js : la modale de filtres mobile, en deux panneaux.
 *
 * Le rail de gauche liste les groupes, le panneau de droite montre les valeurs du groupe
 * choisi. C'est le comportement d'Amazon, et il tient en trois regles :
 *
 *   1. **Le panneau actif est marque sur le `.js-filter` lui-meme**, jamais sur son
 *      contenu. `facets.js` remplace l'innerHTML de chaque `.js-filter` a chaque filtre
 *      applique : une marque posee a l'interieur disparaitrait, et le panneau se refermerait
 *      tout seul des la premiere case cochee.
 *   2. **Tous les `<details>` sont laisses ouverts.** Ce qui montre ou cache un panneau est
 *      une classe CSS, pas l'attribut `open` : sinon le navigateur masquerait le contenu
 *      lui-meme et l'animation de sous-menu de Dawn reprendrait la main.
 *   3. **On recalcule apres chaque rendu**, sur l'evenement `pk:facettes-rendues` emis par
 *      `facets.js`. Sans lui, les pastilles de comptage se figeraient au premier filtre.
 */
class PkMobileFacets extends HTMLElement {
  connectedCallback() {
    if (this.pret) return;
    this.pret = true;

    this.addEventListener('click', (e) => {
      const onglet = e.target.closest('.pk-mfacets__onglet');
      if (!onglet) return;
      e.preventDefault();
      this.activer(onglet.dataset.cible);
    });

    // Les onglets et les deux commandes du bas sont des `<span role="...">` et non des
    // `<button>` : menu-drawer referme le tiroir a chaque clic sur un bouton qu'il
    // contient. Le clavier redevient donc notre responsabilite.
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      const cible = e.target.closest('[role="tab"], [role="button"]');
      if (!cible) return;
      e.preventDefault();
      cible.click();
    });

    // Une case cochee change les pastilles avant meme que le serveur reponde.
    this.addEventListener('change', () => this.rafraichir());
    document.addEventListener('pk:facettes-rendues', () => this.rafraichir());

    this.activer(this.cibleCourante() || this.premiereCible());
  }

  panneaux() {
    return Array.from(this.querySelectorAll('.pk-mfacets__panneaux > .js-filter'));
  }

  onglets() {
    return Array.from(this.querySelectorAll('.pk-mfacets__onglet'));
  }

  premiereCible() {
    const p = this.panneaux()[0];
    return p ? p.dataset.index : null;
  }

  cibleCourante() {
    const actif = this.querySelector('.pk-mfacets__panneaux > .js-filter.pk-mfacets__pane--actif');
    return actif ? actif.dataset.index : null;
  }

  activer(cible) {
    if (!cible) return;
    for (const p of this.panneaux()) {
      const estActif = p.dataset.index === cible;
      p.classList.toggle('pk-mfacets__pane--actif', estActif);
      // `open` est pose sur TOUS : c'est la classe qui montre ou cache, pas le navigateur.
      if (p.tagName === 'DETAILS') p.open = true;
    }
    for (const o of this.onglets()) {
      const estActif = o.dataset.cible === cible;
      o.classList.toggle('pk-mfacets__onglet--actif', estActif);
      o.setAttribute('aria-selected', estActif ? 'true' : 'false');
    }
    const panneau = this.querySelector('.pk-mfacets__panneaux');
    if (panneau) panneau.scrollTop = 0;
    this.rafraichir();
  }

  /** Pastilles du rail : combien de valeurs sont retenues dans chaque groupe. */
  rafraichir() {
    for (const o of this.onglets()) {
      const pastille = o.querySelector('.pk-mfacets__onglet-pastille');
      if (!pastille) continue;
      const panneau = this.querySelector(
        '.pk-mfacets__panneaux > .js-filter[data-index="' + o.dataset.cible + '"]'
      );
      let n = 0;
      if (panneau) {
        n = panneau.querySelectorAll('input[type="checkbox"]:checked').length;
        // Le filtre de prix n'a pas de case a cocher : deux champs, remplis ou non.
        n += Array.from(panneau.querySelectorAll('price-range input')).filter((i) => i.value !== '').length ? 1 : 0;
      }
      pastille.textContent = n > 0 ? String(n) : '';
      pastille.hidden = n === 0;
    }
  }
}

if (!customElements.get('pk-mobile-facets')) {
  customElements.define('pk-mobile-facets', PkMobileFacets);
}

/**
 * Le tiroir de Dawn se referme quand le focus sort de lui (`onFocusOut`). En modale pleine
 * page, un clic sur une etiquette ou sur le rail peut sortir du `<details>` le temps d'un
 * rendu : on rouvre le panneau actif au rendu suivant plutot que de toucher a menu-drawer.
 */
document.addEventListener('pk:facettes-rendues', () => {
  for (const el of document.querySelectorAll('pk-mobile-facets')) {
    if (typeof el.activer === 'function') el.activer(el.cibleCourante() || el.premiereCible());
  }
});

/**
 * Le tri compact du mobile : il PILOTE le <select> de l'en-tete, il ne soumet rien.
 *
 * ⚠️ `onSubmitHandler` (facets.js) ne retient que quatre identifiants de formulaire :
 * `FacetSortForm`, `FacetFiltersForm`, `FacetSortDrawerForm` et `FacetFiltersFormMobile`.
 * Un cinquieme formulaire serait ignore sans un mot : le controle s'ouvrirait, on
 * choisirait un tri, et la page ne bougerait pas. Le compact recopie donc sa valeur dans
 * le `<select>` de l'en-tete et lui envoie un `input` : Dawn reprend exactement le chemin
 * qu'il emprunte deja quand on trie depuis le bureau, et les filtres actifs sont preserves
 * parce que la boucle ramasse aussi `FacetFiltersForm`.
 *
 * ⚠️ L'ECOUTE EST SUR LE DOCUMENT, PAS SUR LE `<select>`, et la cible est relue a chaque
 * fois : `renderAdditionalElements` remplace l'innerHTML de `.sorting` a chaque filtre
 * applique, donc une reference gardee au chargement pointerait un noeud detache des le
 * premier filtre. Le `<form>`, lui, survit : c'est le parent de `.sorting`.
 *
 * ⚠️ Et on vise `#FacetSortForm select`, jamais `getElementById('SortBy')` : Dawn rend
 * DEUX elements portant cet identifiant (celui de l'en-tete et celui du repli sans
 * JavaScript). Le raccourci marcherait aujourd'hui et casserait au premier remaniement de
 * l'ordre du DOM.
 */
const triDeLEntete = () => document.querySelector('#FacetSortForm select[name="sort_by"]');

/**
 * Le resume affiche (« Meilleures ventes ») est recopie DEPUIS L'OPTION RETENUE, jamais
 * recalcule a partir de la valeur : le libelle court vit dans le `<option>`, il n'a donc
 * qu'un seul endroit ou etre juste.
 */
const resumerLeTri = () => {
  const compact = document.getElementById('SortByCompact');
  const texte = document.querySelector('.pk-tri__texte');
  const choisi = compact && compact.selectedOptions[0];
  if (texte && choisi) texte.textContent = choisi.textContent.trim();
};

document.addEventListener('change', (e) => {
  const compact = e.target.closest && e.target.closest('#SortByCompact');
  if (!compact) return;
  // Le resume d'abord : Dawn attend 500 ms avant de partir chercher la page, et une ligne
  // qui ne bouge pas pendant une demi-seconde se lit comme un clic perdu.
  resumerLeTri();
  const source = triDeLEntete();
  if (!source || source.value === compact.value) return;
  source.value = compact.value;
  source.dispatchEvent(new Event('input', { bubbles: true }));
});

/**
 * Le retour arriere du navigateur rejoue un rendu sans passer par le controle : Dawn remet
 * `.sorting` a jour, le compact ne l'est pas. On le realigne a chaque fin de rendu.
 */
document.addEventListener('pk:facettes-rendues', () => {
  const compact = document.getElementById('SortByCompact');
  const source = triDeLEntete();
  if (compact && source && compact.value !== source.value) compact.value = source.value;
  resumerLeTri();
});

/**
 * La bulle de chat Crisp, remontee au-dessus de la barre des boutons.
 *
 * ⚠️ ELLE EST TROUVEE PAR SA GEOMETRIE, PAS PAR SA CLASSE. Crisp nomme ses elements avec
 * des classes generees (`cc-13wro`, `cc-165wh`) qui changent a chaque version : un
 * selecteur ecrit sur l'une d'elles marcherait aujourd'hui et casserait en silence a la
 * mise a jour suivante. On cherche donc, dans `#crisp-chatbox`, l'element **en position
 * fixe** dont la taille est celle d'un lanceur (30 a 90 px de cote).
 *
 * ⚠️ ET LA HAUTEUR EST MESUREE, PAS DEVINEE : la barre des boutons change de hauteur avec
 * la taille de police du visiteur et avec l'encoche du telephone
 * (`env(safe-area-inset-bottom)`). Une valeur en dur laisserait la bulle a cheval sur le
 * bouton chez la moitie des visiteurs.
 */
(() => {
  const lanceur = () => {
    const boite = document.getElementById('crisp-chatbox');
    if (!boite) return null;
    for (const el of boite.querySelectorAll('*')) {
      const cs = getComputedStyle(el);
      if (cs.position !== 'fixed') continue;
      const r = el.getBoundingClientRect();
      if (r.width > 30 && r.width < 90 && r.height > 30 && r.height < 90) return el;
    }
    return null;
  };

  const ajuster = (ouvert) => {
    const el = lanceur();
    if (!el) return;
    el.setAttribute('data-pk-remonte', '');
    if (!ouvert) {
      el.style.transform = '';
      return;
    }
    const barre = document.querySelector('.pk-mfacets__actions');
    const etiquettes = document.querySelector('.pk-mfacets__etiquettes');
    let h = barre ? barre.getBoundingClientRect().height : 0;
    if (etiquettes && etiquettes.children.length) h += etiquettes.getBoundingClientRect().height;
    if (h > 0) el.style.transform = 'translateY(-' + Math.round(h + 8) + 'px)';
  };

  const tiroir = document.querySelector('details.mobile-facets__disclosure');
  if (!tiroir) return;

  // La classe `menu-opening` dit l'etat reel du panneau, y compris quand menu-drawer le
  // referme de lui-meme. L'attribut `open`, lui, reste pose pendant la transition de sortie.
  new MutationObserver(() => ajuster(tiroir.classList.contains('menu-opening'))).observe(tiroir, {
    attributes: true,
    attributeFilter: ['class'],
  });

  // Crisp s'injecte apres le chargement : on retente quelques fois, puis on abandonne.
  let essais = 0;
  const attendre = setInterval(() => {
    if (lanceur() || ++essais > 20) {
      clearInterval(attendre);
      ajuster(tiroir.classList.contains('menu-opening'));
    }
  }, 500);
})();
